import React from 'react';
import styled from 'styled-components';
import { Flex, Modal, Progress, Tag, Typography, Spin } from 'antd';
import {
  ArrowRightOutlined,
  ClockCircleTwoTone,
  CheckCircleTwoTone,
} from '@ant-design/icons';
import { FetchMerchantOnboardingsV2Response } from '@shared/types';
import { t } from '../../../../utils/i18n';
import { useGlobals } from '../../../../globals-context';
import EVENT_BUS_EVENTS from '../../../../constants/event-bus-events';

const STEP_ROUTES = {
  setup_address: '/settings',
  create_service: '/services',
  create_appointment: '/calendar',
  activate_subscription: '/settings/subscription',
  setup_shore_pay: '/settings/payments',
} as const;

const StyledOnboardingProgressModal = styled(Modal)`
  .ant-v5-modal-content {
    padding: 0;
    border-radius: 1.5rem;
    overflow: hidden;
  }

  .ant-v5-modal-footer {
    padding: 2rem 2.25rem;
  }

  .ant-v5-modal-header {
    margin-bottom: 0;
    padding: 1.5rem 2rem;
    background: transparent;
    border-bottom: 1px solid ${({ theme }) => theme.antd.colorBorder};
  }
`;

const StyledList = styled(Flex)``;
const StyledListItem = styled(Flex)`
  cursor: pointer;
  padding: 1.25rem 2rem;
  border-bottom: 1px solid ${({ theme }) => theme.antd.colorBorder};

  &:last-child {
    border-bottom: none;
  }

  &:hover {
    background-color: #f5f5f5;
  }
`;

const StyledListTag = styled(Tag)`
  font-size: 10px;
  padding: 0 0.5rem;
  display: flex;
  line-height: normal;
  align-items: center;
`;

const StyledListStatusSuccessIcon = styled(CheckCircleTwoTone)`
  font-size: 2.75rem;
`;
const StyledListStatusPendingIcon = styled(ClockCircleTwoTone)`
  font-size: 2.75rem;
`;

const StyledListStatusOptionalIcon = styled(ClockCircleTwoTone)`
  font-size: 2.75rem;
`;

const StyledListArrowIcon = styled(ArrowRightOutlined)`
  font-size: 1.5rem;
  color: rgba(0, 0, 0, 0.45) !important;
`;

type OnboardingProgressModalProps = {
  onboardingStepsData: FetchMerchantOnboardingsV2Response | undefined;
  isLoadingOnboardingSteps: boolean;
  openModal: boolean;
  onCloseModalClick: () => void;
};

export function OnboardingProgressModal({
  openModal = false,
  onCloseModalClick,
  onboardingStepsData,
  isLoadingOnboardingSteps,
}: OnboardingProgressModalProps) {
  const { trackMethod, eventBus } = useGlobals() as any;
  const totalTasks = onboardingStepsData?.steps?.length ?? 0;
  const percentageCompleted =
    onboardingStepsData?.overall_progress_percentage ?? 0;
  const completedTasks =
    totalTasks > 0 ? Math.round((percentageCompleted / 100) * totalTasks) : 0;

  const handleOnCloseModalClick = () => {
    trackMethod('onboarding_progress_banner_dismissed', {
      platform: 'booking',
      overall_progress_percentage: percentageCompleted,
    });
    onCloseModalClick();
  };

  const handleOnStepClick = (stepId: string) => {
    trackMethod('onboarding_progress_banner_step_clicked', {
      platform: 'booking',
      step_id: stepId,
      overall_progress_percentage: percentageCompleted,
    });

    const route = STEP_ROUTES[stepId as keyof typeof STEP_ROUTES];

    if (route) {
      eventBus.emit(EVENT_BUS_EVENTS.NAVIGATE, {
        payload: { route },
      });
      onCloseModalClick();
    }
  };

  return (
    <StyledOnboardingProgressModal
      centered
      open={openModal}
      footer={null}
      onCancel={handleOnCloseModalClick}
      title={
        !isLoadingOnboardingSteps ? (
          <Flex gap={10} align="center" justify="flex-start">
            <Flex vertical gap={0}>
              <Progress
                type="circle"
                percent={percentageCompleted}
                strokeColor="#00c2d1"
                size={35}
                format={() => null}
              />
            </Flex>
            <Flex vertical gap={0}>
              <Typography.Title level={4} style={{ margin: 0 }}>
                {t('onboarding-progress-modal.title')}
              </Typography.Title>
              <Typography.Text
                type="secondary"
                style={{ fontSize: 12, fontWeight: 400, margin: 0 }}
              >
                {t('onboarding-progress-modal.description', {
                  completed_tasks: completedTasks,
                  total_tasks: totalTasks,
                })}
              </Typography.Text>
            </Flex>
          </Flex>
        ) : null
      }
    >
      {isLoadingOnboardingSteps && (
        <Flex align="center" justify="center" style={{ minHeight: 150 }}>
          <Spin />
        </Flex>
      )}
      {!isLoadingOnboardingSteps && onboardingStepsData && (
        <StyledList vertical gap={0}>
          {onboardingStepsData.steps.map((step) => (
            <StyledListItem
              key={step.id}
              onClick={() => handleOnStepClick(step.id)}
            >
              <Flex
                gap={10}
                align="center"
                justify="space-between"
                style={{ width: '100%' }}
              >
                <Flex gap={10} align="center">
                  <Flex align="center">
                    {step.completed_at && (
                      <StyledListStatusSuccessIcon twoToneColor="#00d0be" />
                    )}
                    {!step.completed_at && step.is_mandatory && (
                      <StyledListStatusPendingIcon twoToneColor="#a2a2a2" />
                    )}
                    {!step.completed_at && !step.is_mandatory && (
                      <StyledListStatusOptionalIcon twoToneColor="#8bbefc" />
                    )}
                  </Flex>
                  <Flex vertical gap={2}>
                    <Flex gap={10}>
                      <Typography.Text strong>{step.title}</Typography.Text>
                      {!step.is_mandatory && (
                        <StyledListTag color="blue" bordered>
                          {t('onboarding-progress-modal.tags.recommended')}
                        </StyledListTag>
                      )}
                    </Flex>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {step.description}
                    </Typography.Text>
                  </Flex>
                </Flex>
                <StyledListArrowIcon />
              </Flex>
            </StyledListItem>
          ))}
        </StyledList>
      )}
    </StyledOnboardingProgressModal>
  );
}
